// src/comp/NavBar.js
import React, { useState, useContext, useEffect, useMemo} from "react";
import { useTranslation } from "react-i18next";
import localforage from 'localforage';
import { DRAWER_WIDTH, DRAWER_COMPACT_MAX_HEIGHT } from "../../../constants/layout";
import { attentionBadgePopSx, attentionMuiBadgePopSx } from "../../../constants/attentionBadgeMotion";
import { BRAND_PRIMARY } from "../../../constants/brandColors";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  CssBaseline,
  LinearProgress,
  Box,
  Avatar,
  Badge,
  Chip,
  Fade,
  Slide,
  Tooltip,
} from "@mui/material";
import { getMm } from "../../../theme/modernMaterialEnterprise";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import MessageIcon from "@mui/icons-material/Message";
import NewspaperIcon from '@mui/icons-material/Newspaper';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CalendarToday from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GavelIcon from "@mui/icons-material/Gavel";
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import LanguageSwitcher from "../../language/languageSwitcher";
import ThemeSwitch from "../theme/ThemeSwitch";
import { openBugReportDialog } from "../../../components/BugReportHost";
import VersionDialog, { VERSION } from "../VersionDialog";
import { SupabaseUserContext } from "../../../contexts/supabaseUser.context";
import { useTheme } from "../../../contexts/ThemeContext";
import SupabaseClubLogo from "../SupabaseClubLogo";
import SupabaseImageAvatar from "../SupabaseImageAvatar";
import logo from "../../../NewEraLogo.png";
import {
  fetchUnreadMessageCount,
  fetchPosStorefront,
  fetchTrainingProgramMenuVisibility,
  fetchVideoLibraryMenuVisibility,
  fetchClubSettings,
} from "../../../utils/api/api";
import { userIsBoardMember } from "../../../realm/boardAccess";
import { resolveClubId } from "../../../utils/clubScope";
import { memberReviewedForMemberUi, memberCanUseMemberMenus } from "../../../utils/memberAppAccess";
import { userHasAttendanceKioskLogin, getSettingsNavBadgeCount } from "../../../realm/settingsPermissions";
import {
  isNavMenuPageEnabled,
  normalizeNavMenuVisibility,
  NAV_MENU_VISIBILITY_UPDATED_EVENT,
} from "../../../realm/navMenuVisibility";
import { DataContext } from "../../../contexts/DataContext";
import { useClubScope } from "../../../contexts/ClubScopeContext";
import { useActiveMemberSport } from "../../../hooks/useActiveMemberSport";
import { memberShowsBeltRankMenu } from "../../../utils/memberSport";

const drawerWidth = DRAWER_WIDTH;
const mobileDrawerWidth = 320;

/** Viewports at or below ~1080p (browser chrome reduces usable height). */
const SHORT_VIEWPORT_MQ = `(max-height: ${DRAWER_COMPACT_MAX_HEIGHT}px)`;

const NUNITO = "'Plus Jakarta Sans', sans-serif";
const TAB_FONT = '"Plus Jakarta Sans", sans-serif';
const TAB_BLACK = "#1C1B1F";
const TAB_WHITE = "#FFFFFF";
const TAB_DARK_TEXT = "#E8E6EB";

/** Nav palette — matches ScrollableTabBar selected / unselected treatment */
const navTheme = (isDarkMode) =>
  isDarkMode
    ? {
        text: TAB_DARK_TEXT,
        selectedText: "#000000",
        selectedBg: TAB_WHITE,
        hoverBg: "#4E4C52",
      }
    : {
        text: TAB_BLACK,
        selectedText: TAB_WHITE,
        selectedBg: TAB_BLACK,
        hoverBg: "#E0E0E0",
      };

/** Mobile drawer: compact + scroll for staff; larger tap targets for members. */
function resolveDrawerNavLayout({ isMobile, isStaffUser, compactNav, menuCount }) {
  if (!isMobile) {
    return {
      mode: compactNav ? "desktop-compact" : "desktop",
      listDense: compactNav,
      clubLogoSize: compactNav ? 62 : 94,
      clubNameFontSize: compactNav ? "0.8125rem" : "1rem",
      headerPadding: compactNav ? 1.25 : 2.5,
      headerPt: compactNav ? 2.5 : 3.5,
      headerPb: compactNav ? 0.75 : 1.5,
      listPx: compactNav ? 1 : 1.5,
      listPy: compactNav ? 0.25 : 1,
      listPbSafe: 1,
      itemMb: compactNav ? 0.125 : 0.5,
      itemPy: compactNav ? 0.375 : 0.75,
      itemPx: compactNav ? 0.875 : 1.25,
      itemMinHeight: compactNav ? 36 : undefined,
      iconSlotSize: compactNav ? 28 : 36,
      iconFontSize: compactNav ? "1.125rem" : "1.25rem",
      beltIconHeight: compactNav ? 18 : 22,
      labelFontSize: "0.875rem",
      labelLineHeight: 1.5,
      footerPadding: compactNav ? 1 : 2,
      footerLogoWidth: compactNav ? 32 : 45,
    };
  }

  if (isStaffUser) {
    const extraCompact = menuCount >= 10;
    return {
      mode: extraCompact ? "mobile-staff-dense" : "mobile-staff",
      listDense: true,
      clubLogoSize: extraCompact ? 44 : 50,
      clubNameFontSize: "0.75rem",
      headerPadding: 1,
      headerPt: 1.5,
      headerPb: 0.5,
      listPx: 1,
      listPy: 0.25,
      listPbSafe: "calc(12px + env(safe-area-inset-bottom, 0px))",
      itemMb: 0.125,
      itemPy: extraCompact ? 0.35 : 0.45,
      itemPx: 1,
      itemMinHeight: extraCompact ? 38 : 42,
      iconSlotSize: extraCompact ? 26 : 28,
      iconFontSize: extraCompact ? "1.0625rem" : "1.125rem",
      beltIconHeight: extraCompact ? 17 : 18,
      labelFontSize: extraCompact ? "0.8125rem" : "0.875rem",
      labelLineHeight: 1.35,
      footerPadding: 0.75,
      footerLogoWidth: 26,
    };
  }

  return {
    mode: "mobile-member",
    listDense: false,
    clubLogoSize: 64,
    clubNameFontSize: "0.9375rem",
    headerPadding: 1.25,
    headerPt: 2,
    headerPb: 0.75,
    listPx: 1.25,
    listPy: 0.75,
    listPbSafe: "calc(16px + env(safe-area-inset-bottom, 0px))",
    itemMb: 0.35,
    itemPy: 1,
    itemPx: 1.25,
    itemMinHeight: 48,
    iconSlotSize: 34,
    iconFontSize: "1.375rem",
    beltIconHeight: 22,
    labelFontSize: "1rem",
    labelLineHeight: 1.4,
    footerPadding: 1,
    footerLogoWidth: 32,
  };
}

const NavBar = ({ drawerOpen, setDrawerOpen, userInfo, selectedPage, setSelectedPage, isLoading, contentDrawerWidth = DRAWER_WIDTH, restrictMenuToHome = false, allowedMenuPages = null }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isShortViewport = useMediaQuery(SHORT_VIEWPORT_MQ);
  // Enhanced iPad detection - covers various iPad models and orientations
  const isIPad = useMediaQuery([
    "(min-width:768px) and (max-width:1024px) and (-webkit-min-device-pixel-ratio: 2)",
    "(min-width:768px) and (max-width:1024px) and (orientation: landscape)",
    "(min-width:768px) and (max-width:1024px) and (orientation: portrait)",
    "(min-width:810px) and (max-width:1080px) and (-webkit-min-device-pixel-ratio: 2)"
  ].join(", "));
  /** Attendance kiosk permission: overlay menu that closes after navigation (QR scanner iPad) */
  const isAttendanceKiosk = userHasAttendanceKioskLogin(userInfo);
  const navTabletLayout = isIPad || isAttendanceKiosk;
  const navOverlayDrawer = isMobile || isAttendanceKiosk;
  /** Denser drawer on desktop/laptop at 1080p and similar heights */
  const compactNav = !isMobile && (isShortViewport || navTabletLayout);
  const { logOutUser } = useContext(SupabaseUserContext);
  const {
    unreviewedMembersCount,
    pendingParentRequestsCount,
    pendingInstructorAttendancesCount,
    adultPromotions,
    childPromotions,
    unpublishedBeltPromotions,
    pendingAnmalningSubmissionsCount,
    isLoading: badgeDataLoading,
  } = useContext(DataContext);
  const { sport: activeMemberSport, loaded: activeMemberSportLoaded } = useActiveMemberSport(userInfo);
  const { apiMe } = useClubScope();
  const inInstructorsGroup = apiMe?.inInstructorsGroup === true;
  const showBeltMenu = memberShowsBeltRankMenu(activeMemberSport, { loaded: activeMemberSportLoaded });
  
  // Cache-busting key for images - regenerates when userInfo changes
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  
  // Version dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  // Unread message count for badge on Messages nav item
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [posShopVisible, setPosShopVisible] = useState(false);
  const [trainingProgramMenuVisible, setTrainingProgramMenuVisible] = useState(true);
  const [videoLibraryMenuVisible, setVideoLibraryMenuVisible] = useState(false);
  const [navMenuVisibility, setNavMenuVisibility] = useState(() => normalizeNavMenuVisibility(null));
  /** Canonical club display name from clubsettings (falls back to member row). */
  const [drawerClubName, setDrawerClubName] = useState(null);

  // Check if connected to localhost
  const isLocalhost = () => {
    const apiUrl = process.env.REACT_APP_CONNECTION_API || '';
    return apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');
  };

  // Check if user is admin
  const isAdmin = userInfo[0]?.isAdmin || false;
  const isCoach = userInfo[0]?.isCoach || false;

  const settingsAttentionCount = useMemo(() => {
    if (badgeDataLoading || !(isAdmin || isCoach)) return 0;
    return getSettingsNavBadgeCount(userInfo, {
      unreviewedMembersCount,
      pendingParentRequestsCount,
      pendingInstructorAttendancesCount,
      adultPromotions,
      childPromotions,
      unpublishedBeltPromotions,
      pendingAnmalningSubmissionsCount,
    });
  }, [
    badgeDataLoading,
    isAdmin,
    isCoach,
    userInfo,
    unreviewedMembersCount,
    pendingParentRequestsCount,
    pendingInstructorAttendancesCount,
    adultPromotions,
    childPromotions,
    unpublishedBeltPromotions,
    pendingAnmalningSubmissionsCount,
  ]);

  const navStudentId = userInfo?.[0]?.studentID;
  const navReviewed = memberReviewedForMemberUi(userInfo?.[0]);
  const navMemberMenus = memberCanUseMemberMenus(userInfo?.[0]);
  const navClubId = userInfo?.[0]?.clubID;
  const navClubIdAlt = userInfo?.[0]?.clubId;
  const scopedClubId = resolveClubId(userInfo);

  // Show localhost chip only for admins connected to localhost
  const showLocalhostChip = isAdmin && isLocalhost();
  
  // Regenerate image refresh key when userInfo changes to ensure fresh profile images
  useEffect(() => {
    if (userInfo && navStudentId) {
      setImageRefreshKey(Date.now());
    }
  }, [userInfo, navStudentId]);

  // Fetch unread message count on mount, when user opens MessagePage (to refresh after viewing),
  // when tab gains focus (so badge updates when switching back from another tab),
  // and periodically so badge updates when new messages arrive from any sender (admin, mentor, user).
  // Works for all users (admin, mentor, regular) - pass active user's studentID and clubID when available.
  useEffect(() => {
    if (!navReviewed || !navStudentId) return;
    const sid = navStudentId;
    const cid = navClubId ?? navClubIdAlt ?? null;
    const refresh = () => fetchUnreadMessageCount(sid, cid).then(setUnreadMessageCount);
    refresh();
    // When user opens MessagePage, refresh again after a delay so badge updates after MessagePage marks messages as read
    const delayRefresh = selectedPage === 'MessagePage' ? setTimeout(refresh, 2000) : null;
    const interval = setInterval(refresh, 60000); // Poll every 60s for new messages
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (delayRefresh) clearTimeout(delayRefresh);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [navStudentId, navReviewed, navClubId, navClubIdAlt, selectedPage]);

  useEffect(() => {
    if (!navReviewed) {
      setPosShopVisible(false);
      return;
    }
    fetchPosStorefront(scopedClubId)
      .then((sf) => setPosShopVisible(Boolean(sf?.visibleInMenu)))
      .catch(() => setPosShopVisible(false));
  }, [navReviewed, scopedClubId, selectedPage]);

  useEffect(() => {
    if (!navReviewed) {
      setTrainingProgramMenuVisible(false);
      return;
    }
    fetchTrainingProgramMenuVisibility(scopedClubId)
      .then((data) => setTrainingProgramMenuVisible(Boolean(data?.visibleInMenu)))
      .catch(() => setTrainingProgramMenuVisible(true));
  }, [navReviewed, scopedClubId, selectedPage]);

  useEffect(() => {
    if (!navReviewed) {
      setVideoLibraryMenuVisible(false);
      return;
    }
    fetchVideoLibraryMenuVisibility(scopedClubId)
      .then((data) => setVideoLibraryMenuVisible(Boolean(data?.visibleInMenu)))
      .catch(() => setVideoLibraryMenuVisible(false));
  }, [navReviewed, scopedClubId, selectedPage]);

  useEffect(() => {
    if (!scopedClubId) {
      setNavMenuVisibility(normalizeNavMenuVisibility(null));
      setDrawerClubName(null);
      return;
    }
    let cancelled = false;

    const applyClubSettings = (data) => {
      if (cancelled) return;
      setNavMenuVisibility(normalizeNavMenuVisibility(data?.navMenuVisibility));
      const name = data?.clubname != null ? String(data.clubname).trim() : "";
      setDrawerClubName(name || null);
    };

    const loadClubSettings = () => {
      fetchClubSettings(scopedClubId)
        .then((data) => applyClubSettings(data))
        .catch(() => {
          if (!cancelled) {
            setNavMenuVisibility(normalizeNavMenuVisibility(null));
            setDrawerClubName(null);
          }
        });
    };

    loadClubSettings();

    const onNavMenuUpdated = (event) => {
      const detail = event?.detail;
      if (detail?.clubID != null && String(detail.clubID) !== String(scopedClubId)) return;
      if (detail?.navMenuVisibility) {
        setNavMenuVisibility(normalizeNavMenuVisibility(detail.navMenuVisibility));
      }
    };

    const onClubBrandingUpdated = (event) => {
      const detail = event?.detail;
      if (detail?.clubID != null && String(detail.clubID) !== String(scopedClubId)) return;
      loadClubSettings();
    };

    window.addEventListener(NAV_MENU_VISIBILITY_UPDATED_EVENT, onNavMenuUpdated);
    window.addEventListener("clubLogoUpdated", onClubBrandingUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(NAV_MENU_VISIBILITY_UPDATED_EVENT, onNavMenuUpdated);
      window.removeEventListener("clubLogoUpdated", onClubBrandingUpdated);
    };
  }, [scopedClubId, selectedPage]);

  const menuOn = (page) =>
    isNavMenuPageEnabled(navMenuVisibility, userInfo?.[0], page, { inInstructorsGroup });

  const menuItems = [
    {
      text: t("home_page"), 
      icon: <HomeIcon />, 
      page: "LandingPage"
    },
    (userInfo[0]?.isAdmin || userInfo[0]?.isCoach) &&
    menuOn("SettingsPage") &&
    { 
      text: t("admin_settings"), 
      icon: <SettingsIcon />,
      page: "SettingsPage",
      badgeCount: settingsAttentionCount,
    },

    userIsBoardMember(userInfo[0]) &&
    userInfo[0]?.reviewed &&
    {
      text: t("board_page_title"),
      icon: <GavelIcon />,
      page: "BoardPage",
    },

    navMemberMenus &&
    menuOn("ProfilePage") &&
    { 
      text: t("my_profile"), 
      icon: <AccountCircleIcon />, 
      page: "ProfilePage" },

    navReviewed && (userInfo[0]?.isStudent || userInfo[0]?.hasParent) && showBeltMenu &&
    menuOn("ProfileBeltsPage") &&
    {
      text: t("belt"),
      icon: (
        <Box
          component="img"
          src="belt_black.png"
          alt="belt"
        />
      ),
      page: "ProfileBeltsPage"
    },
    navReviewed &&
    menuOn("MessagePage") &&
    { 
      text: t("messages"), 
      icon: unreadMessageCount > 0 ? (
        <Badge badgeContent={unreadMessageCount} color="error" sx={attentionMuiBadgePopSx}>
          <MessageIcon />
        </Badge>
      ) : <MessageIcon />, 
      page: "MessagePage" },
    navReviewed &&
    menuOn("NewsPage") &&
    { 
      text: t("news"), 
      icon: <NewspaperIcon />, 
      page: "NewsPage" },
    trainingProgramMenuVisible &&
    navReviewed &&
    menuOn("TrainingProgramPage") &&
    {
      text: t("training_program"),
      icon: <FitnessCenterIcon />,
      page: "TrainingProgramPage",
    },
    videoLibraryMenuVisible &&
    navReviewed &&
    menuOn("VideoLibraryPage") &&
    {
      text: t("video_library_title"),
      icon: <OndemandVideoIcon />,
      page: "VideoLibraryPage",
    },
    posShopVisible &&
    navReviewed &&
    menuOn("PosShopPage") &&
    {
      text: t("pos_shop_title"),
      icon: <StorefrontIcon />,
      page: "PosShopPage",
    },
    userInfo[0]?.isColorPromotion === true && 
    { 
      text: t("color_promotion"), 
      icon: <MilitaryTechIcon />, 
      page: "ProfileColorPromotionPage" },
    (navMemberMenus || userInfo[0]?.rejected) &&
    menuOn("SchedulePage") &&
    { 
      text: t("schedule_title"), 
      icon: <CalendarToday />, 
      page: "SchedulePage" 
    },
    (userInfo[0]?.reviewed || userInfo[0]?.rejected) && userInfo[0]?.isCoach === true &&
    menuOn("InstructorSchedulePage") &&
    { 
      text: t("instructors_agenda"), 
      icon: <CalendarToday />, 
      page: "InstructorSchedulePage" 
    },
    (userInfo[0]?.reviewed || userInfo[0]?.rejected) &&
    (userInfo[0]?.isCoach === true || userInfo[0]?.isAdmin || inInstructorsGroup) &&
    menuOn("InstructorAvailabilityPage") &&
    {
      text: t("instructor_availability_title"),
      icon: <PeopleIcon />,
      page: "InstructorAvailabilityPage"
    },
    navReviewed &&
    menuOn("DigitalCardPage") &&
    { 
      text: t("membership_card"), 
      icon: <CardMembershipIcon />, 
      page: "DigitalCardPage" 

    },
    navReviewed &&
    menuOn("DocumentationPage") &&
    { 
      text: t("documentation_title"), 
      icon: <MenuBookOutlinedIcon />, 
      page: "DocumentationPage" 
    },
  ].filter(Boolean);

  const restrictedPages = restrictMenuToHome
    ? ["LandingPage"]
    : Array.isArray(allowedMenuPages) && allowedMenuPages.length > 0
      ? allowedMenuPages
      : null;

  const visibleMenuItems = restrictedPages
    ? restrictedPages
        .map((page) => {
          const existing = menuItems.find((item) => item.page === page);
          if (existing) return existing;
          if (page === "LandingPage") {
            return { text: t("home_page"), icon: <HomeIcon />, page: "LandingPage" };
          }
          if (page === "DigitalCardPage") {
            return { text: t("membership_card"), icon: <CardMembershipIcon />, page: "DigitalCardPage" };
          }
          return null;
        })
        .filter(Boolean)
    : menuItems;

  const isStaffUser = isAdmin || isCoach;
  const drawerNav = useMemo(
    () =>
      resolveDrawerNavLayout({
        isMobile,
        isStaffUser,
        compactNav,
        menuCount: visibleMenuItems.length,
      }),
    [isMobile, isStaffUser, compactNav, visibleMenuItems.length]
  );

  const handleNav = (page) => {
    setSelectedPage(page);
    if (navOverlayDrawer) {
      setDrawerOpen(false);
    }
  };

  const logOut = async () => {
    try {
      await Promise.all([localforage.removeItem('activeUser'), localforage.removeItem('userInfo')]);
    } catch { }
    localStorage.removeItem('club');
    localStorage.removeItem('email');
    localStorage.removeItem('name');
    localStorage.removeItem('realmJwt');
    try { 
      await logOutUser(); 
      // Redirect to login page after successful logout
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to login page
      window.location.href = '/login';
    }
  };

  return (
    <>
      <CssBaseline />
      
      {/* Version Dialog */}
      <VersionDialog 
        open={versionDialogOpen} 
        onClose={() => setVersionDialogOpen(false)} 
      />


      
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: getMm(isDarkMode).surfaceLowest, 
          zIndex: (t) => t.zIndex.drawer + 1,
          boxShadow: 'none',
          borderBottom: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          borderRadius: 0,
          fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }}
      >
        <Toolbar sx={{ 
          position: 'relative',
          justifyContent: "space-between", 
          alignItems: "center",
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 2, sm: 3 }
        }}>
          {/* Menu Button - Admin Dashboard Style */}
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => setDrawerOpen(o => !o)}
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '8px',
              p: 1.5,
              color: getMm(isDarkMode).onSurface,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, color: getMm(isDarkMode).onSurface }} />
          </IconButton>

          {/* Spacer - reserves center area */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />

          {/* Logo - centered over main content below (adjusts when drawer open/closed) */}
          <Box
            onClick={() => handleNav("LandingPage")}
            sx={{
              position: 'absolute',
              left: (navOverlayDrawer || !drawerOpen)
                ? '50%'
                : `calc(${contentDrawerWidth}px + (100% - ${contentDrawerWidth}px) / 2)`,
              transform: 'translateX(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              zIndex: 1,
              pointerEvents: 'auto'
            }}
          >
            <img 
              src={isDarkMode ? logo : '/NewEraBlackLogo.png'} 
              alt="New Era Logo" 
              style={{ 
                height: isMobile ? 35 : 45
              }} 
            />
          </Box>

          {/* User Profile Info - Admin Dashboard Style */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            mr: 1
          }}>
            <SupabaseImageAvatar
              user={userInfo[0]}
              size={32}
              refreshKey={imageRefreshKey}
              sx={{
                border: '2px solid',
                borderColor: getMm(isDarkMode).outlineVariant
              }}
            />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: getMm(isDarkMode).onSurface,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}
            >
              {userInfo[0]?.name?.split(' ')[0] || 'User'}
            </Typography>
          </Box>

          {/* Logout Button - Admin Dashboard Style */}
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={logOut}
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '8px',
              p: 1,
              color: getMm(isDarkMode).onSurface,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <ExitToAppIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, color: getMm(isDarkMode).onSurface }} />
          </IconButton>
        </Toolbar>
        {isLoading && (
          <LinearProgress 
            sx={{ 
              height: 3,
              backgroundColor: getMm(isDarkMode).outlineVariant,
              '& .MuiLinearProgress-bar': {
                backgroundColor: BRAND_PRIMARY.main
              }
            }} 
          />
        )}
      </AppBar>

      <Drawer
        variant={navOverlayDrawer ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={navOverlayDrawer ? () => setDrawerOpen(false) : undefined}
                 PaperProps={{ 
           sx: { 
             width: isMobile ? '100%' : (navTabletLayout ? mobileDrawerWidth : drawerWidth), 
             backgroundColor: getMm(isDarkMode).surfaceLowest,
             borderRight: isDarkMode ? '1px solid #374151' : '1px solid #e2e2ea',
             boxShadow: 'none',
             borderRadius: 0,
             fontFamily: NUNITO,
             height: '100%',
             maxHeight: '100dvh',
           } 
         }}
        TransitionComponent={Slide}
        transitionDuration={300}
      >
                 <Box sx={{ 
           display: "flex", 
           flexDirection: "column", 
           height: "100%",
           maxHeight: "100dvh",
           pt: compactNav ? 7 : { xs: 7, sm: 9 },
           overflow: "hidden",
           boxSizing: "border-box",
         }}>
                     {/* Club Logo + name — sticky so always visible while scrolling nav */}
           <Fade in={true} timeout={800}>
             <Box sx={{ 
               flexShrink: 0,
               textAlign: "center", 
               p: drawerNav.headerPadding,
               pt: drawerNav.headerPt,
               pb: drawerNav.headerPb,
               backgroundColor: getMm(isDarkMode).surfaceLowest,
               borderBottom: isDarkMode ? '1px solid #374151' : '1px solid #e2e2ea',
             }}>
               <SupabaseClubLogo
                 clubID={scopedClubId ?? userInfo[0]?.clubID}
                 size={drawerNav.clubLogoSize}
                 sx={{
                   margin: "0 auto 4px",
                 }}
               />
               <Typography sx={{ 
                 color: isDarkMode ? '#FFFFFF' : '#1a1b21',
                 fontWeight: 700,
                 fontSize: drawerNav.clubNameFontSize,
                 lineHeight: 1.25,
                 fontFamily: NUNITO,
                 px: 1,
                 wordBreak: "break-word",
               }}>
                 {drawerClubName || userInfo[0]?.clubname || "NewEra"}
               </Typography>
             </Box>
           </Fade>

                     {/* Navigation Menu — scrollable (staff: compact list; members: larger rows) */}
           <List
             dense={drawerNav.listDense}
             sx={{ 
             px: drawerNav.listPx, 
             py: drawerNav.listPy,
             pb: drawerNav.listPbSafe,
             flex: 1,
             overflowY: "auto",
             overflowX: "hidden",
             width: "100%",
             minHeight: 0,
             WebkitOverflowScrolling: "touch",
             overscrollBehavior: "contain",
           }}>
            {visibleMenuItems.map((item, index) => {
              const isSelected = selectedPage === item.page;
              const nav = navTheme(isDarkMode);
              const iconSize = drawerNav.iconSlotSize;
              return (
              <ListItem 
                button 
                key={index} 
                onClick={() => handleNav(item.page)}
                sx={{
                  position: "relative",
                  width: "100%",
                  borderRadius: "8px",
                  mb: drawerNav.itemMb,
                  py: drawerNav.itemPy,
                  px: drawerNav.itemPx,
                  minHeight: drawerNav.itemMinHeight,
                  backgroundColor: isSelected ? nav.selectedBg : "transparent",
                  border: "none",
                  boxShadow: "none",
                  fontFamily: TAB_FONT,
                  transition: "background-color 0.15s ease",
                  overflow: "hidden",
                  "&:hover": {
                    backgroundColor: isSelected ? nav.selectedBg : nav.hoverBg,
                  },
                }}
              >
                <ListItemIcon sx={{ 
                   color: isSelected ? nav.selectedText : nav.text,
                   cursor: "pointer",
                   minWidth: iconSize + 12,
                   mr: 0.5,
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   width: iconSize,
                   height: iconSize,
                   borderRadius: "8px",
                   backgroundColor: "transparent",
                   transition: "color 0.15s ease",
                   '& .MuiSvgIcon-root': {
                     fontSize: drawerNav.iconFontSize,
                     color: isSelected ? nav.selectedText : nav.text,
                   },
                   '& .MuiBadge-root .MuiSvgIcon-root': {
                     color: isSelected ? nav.selectedText : nav.text,
                   },
                   '& img': {
                     height: drawerNav.beltIconHeight,
                     width: "auto",
                     maxWidth: "100%",
                     objectFit: "contain",
                     display: "block",
                     filter: isSelected !== isDarkMode
                       ? "brightness(0) invert(1)"
                       : "brightness(0)",
                     opacity: 1,
                     transition: "filter 0.15s ease",
                   },
                 }}>
                  {item.icon}
                </ListItemIcon>
                 <ListItemText 
                   sx={{ 
                     color: isSelected ? nav.selectedText : nav.text,
                     cursor: "pointer",
                     width: '100%',
                     minWidth: 0,
                     fontFamily: TAB_FONT,
                     '& .MuiTypography-root': {
                       fontWeight: 700,
                       fontSize: drawerNav.labelFontSize,
                       fontFamily: TAB_FONT,
                       color: isSelected ? nav.selectedText : nav.text,
                       letterSpacing: 0,
                       lineHeight: drawerNav.labelLineHeight,
                       whiteSpace: "normal",
                       wordBreak: "break-word",
                     }
                   }} 
                   primary={item.text} 
                 />
                {item.badgeCount > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      flexShrink: 0,
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      color: "#fff",
                      bgcolor: "error.main",
                      ...attentionBadgePopSx,
                    }}
                  >
                    {item.badgeCount}
                  </Box>
                ) : null}
              </ListItem>
            );})}
          </List>

          {/* Drawer footer — pinned below scrollable nav */}
          <Box sx={{ flexShrink: 0, backgroundColor: getMm(isDarkMode).surfaceLowest }}>
          {/* Version Info + theme / language (minimal icons on all sizes) */}
          <Box sx={{ 
            textAlign: "center", 
            px: isMobile ? 1.5 : 1.25, 
            pb: isMobile ? 0.5 : 0.75,
            pt: 0.5,
            borderTop: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.75,
                mb: 0.5,
              }}
            >
              <Tooltip title={t("bug_report_fab_tooltip")} arrow placement="top">
                <IconButton
                  size="small"
                  aria-label={t("bug_report_title")}
                  onClick={() => openBugReportDialog()}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                    backgroundColor: getMm(isDarkMode).surfaceContainer,
                    color: getMm(isDarkMode).onSurfaceVariant,
                    "&:hover": {
                      backgroundColor: getMm(isDarkMode).outlineVariant,
                    },
                  }}
                >
                  <BugReportOutlinedIcon sx={{ fontSize: "1.125rem" }} />
                </IconButton>
              </Tooltip>
              <ThemeSwitch userInfo={userInfo} minimal />
              <LanguageSwitcher minimal />
            </Box>
            <Typography 
              sx={{ 
                color: getMm(isDarkMode).onSurface,
                cursor: "pointer",
                fontSize: '0.625rem',
                display: 'block',
                mb: showLocalhostChip && !isMobile ? 0.75 : 0,
                fontFamily: NUNITO
              }}
              onClick={() => setVersionDialogOpen(true)}
            >
              {t('version_label')}: {VERSION}
            </Typography>
            
            {/* Localhost Connection Chip - Only for Admins */}
            {showLocalhostChip && !isMobile && (
              <Box sx={{ mt: 0.25 }}>
                <Chip 
                  label={t('localhost_server')}
                  size="small"
                  sx={{ 
                    backgroundColor: isDarkMode ? '#374151' : '#F59E0B',
                    color: 'white',
                    border: isDarkMode ? '2px solid #374151' : '2px solid #F59E0B',
                    fontSize: '0.65rem',
                    height: '20px',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ 
            textAlign: "center", 
            p: drawerNav.footerPadding,
            pt: isMobile ? 0.5 : (compactNav ? 0.5 : undefined),
            pb: isMobile
              ? "calc(8px + env(safe-area-inset-bottom, 0px))"
              : drawerNav.footerPadding,
            borderTop: compactNav && !isMobile ? 'none' : (`1px solid ${getMm(isDarkMode).outlineVariant}`)
          }}>
            <a 
              href="https://www.webappsolutions.se" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <img 
                src="/031WS.png" 
                alt="031 Web Solutions" 
                style={{ 
                  width: drawerNav.footerLogoWidth, 
                  opacity: 0.7
                }}
              />
            </a>
          </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;
